import { useCallback, useState, useEffect } from 'react';

import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { createContainer, useContainer } from 'unstated-next';

import { GET_CLASS_SESSION_DASHBOARD_DATA } from '../../../../../config/apiPaths';
import { getUserUuidHeaders } from '../../../../../utils/httpUtils';
import { getUrlParams, getUrls, processApiPath } from '../../../../../utils/url';
import AdminAuthContainer from '../../../AdminAuthContainer';

interface ClassSessionContainerState {
    classSessionData?: Record<string, any>; // result of the GET_CLASS_DATA_PATH api call
}

type LoadingStatus = 'INITIAL' | 'LOADING' | 'SUCCESS' | 'ERROR';

const useClassSessionContainer = (state: ClassSessionContainerState = {}) => {
    console.log('ClassSessionContainer');
    const { api } = getUrls();

    const [classSessionData, setClassSessionData] = useState<any>();

    const { t: tCommon } = useTranslation('common');

    const { uuid, authMode, showDemoContent } = useContainer(AdminAuthContainer);

    const [getClassSessionStatus, setGetClassSessionStatus] = useState<LoadingStatus>('INITIAL');
    const [errorMessage, setErrorMessage] = useState<string>('');

    const { classSessionUuid: classSessionUuidV1 } = useParams<{ classSessionUuid?: string }>();
    const { class_session_id: classSessionUuidV2 } = getUrlParams();
    const classSessionUuid = classSessionUuidV1 || classSessionUuidV2;

    const returnErrorMessage = useCallback(
        (error: any): string => {
            console.error(error);
            return tCommon('errorMessage');
        },
        [tCommon]
    );

    const getClassSessionDataCall = useCallback(async () => {
        const { data: responseData } = await axios.get(
            processApiPath(GET_CLASS_SESSION_DASHBOARD_DATA, undefined, {
                class_session_uuid: classSessionUuid!,
                auth_mode: authMode,
                demo_content: String(showDemoContent)
            }),
            { headers: getUserUuidHeaders(uuid as string) }
        );

        // If the data set is not pulled from S3 as a result of a too-large case, give the actual response
        if (!responseData.response_too_large_url) {
            return responseData;
        }
        const { data } = await axios.get(responseData.response_too_large_url);
        if (data?.success === false) {
            throw Error(data?.error);
        }
        return data;
    }, [classSessionUuid, authMode, uuid, showDemoContent]);

    const getClassSession = useCallback(async () => {
        setGetClassSessionStatus('LOADING');

        try {
            const data = state.classSessionData ?? (await getClassSessionDataCall());
            setClassSessionData(data);
            setGetClassSessionStatus('SUCCESS');
        } catch (err) {
            const errorMsg = returnErrorMessage(err);
            setErrorMessage(errorMsg);
            setGetClassSessionStatus('ERROR');
        }
    }, [state.classSessionData, getClassSessionDataCall, returnErrorMessage]);

    useEffect(() => {
        if (classSessionUuid) {
            getClassSession();
        }
    }, [getClassSession, classSessionUuid]);

    return {
        classSessionUuid,
        api,
        classSessionData,
        getClassSessionStatus,
        errorMessage
    };
};

const ClassSessionContainer = createContainer(useClassSessionContainer);

export default ClassSessionContainer;
